import { useCallback, useMemo, useRef, useState } from 'react'
import type {
  BusinessEntity,
  DocumentCategory,
  DocumentFolder,
  DocumentMeta,
  FinancialAccount,
} from '../../types/family-finance'
import { FOLDER_CATEGORIES, FOLDER_ICONS, FOLDER_LABELS } from '../../types/family-finance'
import { uploadDocument } from '../../lib/family-finance-api'
import { routeDocument, suggestDisplayName, type RouteSuggestion } from '../../lib/document-router'
import { MAX_FILE_BYTES } from '../../lib/document-blob-store'
import { Chip, inputClass, primaryBtn, secondaryBtn } from './shared'

const TAX_YEARS = [2026, 2025, 2024, 2023, 2022, 2021]

type StagedStatus = 'ready' | 'uploading' | 'done' | 'error'

interface Staged {
  key: string
  file: File
  meta: DocumentMeta
  suggestion: RouteSuggestion
  status: StagedStatus
  error: string | null
}

const CONFIDENCE_TONE = { high: 'green', medium: 'blue', low: 'amber' } as const

/** Keep the category valid when the folder changes. */
function categoryFor(folder: DocumentFolder, current: DocumentCategory): DocumentCategory {
  const allowed = FOLDER_CATEGORIES[folder]
  return allowed.includes(current) ? current : allowed[allowed.length - 1]
}

export default function DocumentUploader({
  entities,
  accounts,
  onUploaded,
  lockTo,
  hint,
}: {
  entities: BusinessEntity[]
  accounts: FinancialAccount[]
  onUploaded: () => void
  /**
   * Pins part of the destination — the business editor pins
   * `{ folder: 'businesses', entityId }` so uploads land on that business.
   */
  lockTo?: Partial<DocumentMeta>
  hint?: string
}) {
  const [staged, setStaged] = useState<Staged[]>([])
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [rejected, setRejected] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const folderLocked = lockTo?.folder !== undefined
  const entityLocked = lockTo?.entityId !== undefined
  const accountLocked = lockTo?.accountId !== undefined

  const accountLabel = useMemo(() => {
    const map = new Map(accounts.map((a) => [a.id, `${a.institutionName} — ${a.name}`]))
    return (id: string | null) => (id ? map.get(id) ?? id : '')
  }, [accounts])

  const stage = useCallback(
    (files: FileList | File[]) => {
      const tooBig: string[] = []
      const next: Staged[] = []
      Array.from(files).forEach((file, i) => {
        if (file.size > MAX_FILE_BYTES) {
          tooBig.push(`${file.name} (${Math.round(file.size / 1048576)} MB)`)
          return
        }
        const suggestion = routeDocument(file.name, { entities, accounts })
        const folder = (lockTo?.folder ?? suggestion.folder) as DocumentFolder
        next.push({
          key: `${Date.now()}-${i}-${file.name}`,
          file,
          suggestion,
          status: 'ready',
          error: null,
          meta: {
            folder,
            category: categoryFor(folder, suggestion.category),
            name: suggestDisplayName(file.name),
            taxYear: suggestion.taxYear,
            entityId: suggestion.entityId,
            accountId: suggestion.accountId,
            source: suggestion.source,
            notes: '',
            ...lockTo,
          },
        })
      })
      setRejected(tooBig)
      setStaged((prev) => [...prev, ...next])
    },
    [accounts, entities, lockTo],
  )

  function patch(key: string, meta: Partial<DocumentMeta>) {
    setStaged((prev) =>
      prev.map((s) => {
        if (s.key !== key) return s
        const merged = { ...s.meta, ...meta }
        if (meta.folder && meta.folder !== s.meta.folder) {
          merged.category = categoryFor(meta.folder, s.meta.category)
        }
        return { ...s, meta: merged, error: null }
      }),
    )
  }

  function remove(key: string) {
    setStaged((prev) => prev.filter((s) => s.key !== key))
  }

  async function uploadAll() {
    setUploading(true)
    let anySucceeded = false
    // Sequential: uploads are user-visible and a failure mid-batch should stop
    // at a known point rather than leave a scatter of half-filed documents.
    for (const item of staged.filter((s) => s.status !== 'done')) {
      setStaged((prev) =>
        prev.map((s) => (s.key === item.key ? { ...s, status: 'uploading', error: null } : s)),
      )
      try {
        await uploadDocument(item.file, item.meta)
        anySucceeded = true
        setStaged((prev) => prev.map((s) => (s.key === item.key ? { ...s, status: 'done' } : s)))
      } catch (err) {
        setStaged((prev) =>
          prev.map((s) =>
            s.key === item.key
              ? { ...s, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' }
              : s,
          ),
        )
      }
    }
    setUploading(false)
    if (anySucceeded) onUploaded()
    // Clear the finished ones; anything that errored stays for a retry.
    setStaged((prev) => prev.filter((s) => s.status !== 'done'))
  }

  const pending = staged.filter((s) => s.status !== 'done').length
  const needsDestination = staged.filter(
    (s) => s.meta.folder === 'businesses' && !s.meta.entityId,
  ).length

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          if (e.dataTransfer.files.length) stage(e.dataTransfer.files)
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
        }}
        className={`rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-colors ${
          dragging
            ? 'border-blue-500 bg-blue-500/10'
            : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 bg-slate-100/50 dark:bg-slate-900/50'
        }`}
      >
        <div className="text-3xl mb-2">📤</div>
        <p className="text-slate-800 dark:text-slate-200 text-sm font-medium">
          Drop files here, or click to browse
        </p>
        <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
          {hint ??
            'Each file is filed into Taxes, Businesses, or Accounts based on its name — review before uploading.'}
        </p>
        <p className="text-slate-400 dark:text-slate-600 text-xs mt-1">
          PDF, images, spreadsheets, CSV · up to {MAX_FILE_BYTES / 1048576} MB each
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.heic,.webp,.csv,.xls,.xlsx,.doc,.docx,.txt,.ofx,.qfx"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) stage(e.target.files)
            e.target.value = '' // let the same file be re-picked after a removal
          }}
        />
      </div>

      {rejected.length > 0 && (
        <p className="text-amber-600 dark:text-amber-400 text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
          Skipped — over the {MAX_FILE_BYTES / 1048576} MB limit: {rejected.join(', ')}
        </p>
      )}

      {/* Staged files */}
      {staged.length > 0 && (
        <div className="space-y-3">
          {staged.map((item) => (
            <div
              key={item.key}
              className={`rounded-xl border p-3 ${
                item.status === 'error'
                  ? 'border-red-500/40 bg-red-500/5'
                  : 'border-slate-200 dark:border-slate-700 bg-slate-100/50 dark:bg-slate-900/50'
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <p className="text-slate-900 dark:text-white text-sm font-medium truncate">
                    {item.file.name}
                  </p>
                  <p className="text-slate-500 text-xs">
                    {Math.max(1, Math.round(item.file.size / 1024)).toLocaleString()} KB ·{' '}
                    {item.file.type || 'unknown type'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Chip tone={CONFIDENCE_TONE[item.suggestion.confidence]}>
                    {FOLDER_ICONS[item.meta.folder]} {FOLDER_LABELS[item.meta.folder]}
                  </Chip>
                  {item.status === 'uploading' ? (
                    <span className="text-blue-600 dark:text-blue-400 text-xs">Uploading…</span>
                  ) : (
                    <button
                      onClick={() => remove(item.key)}
                      className="text-slate-500 hover:text-red-600 dark:hover:text-red-400 text-sm px-1"
                      title="Remove from this batch"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              <p className="text-slate-500 dark:text-slate-400 text-xs mb-3">
                🧭 {item.suggestion.reason}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <label className="block sm:col-span-2">
                  <span className="text-slate-500 text-xs">Document name</span>
                  <input
                    value={item.meta.name}
                    onChange={(e) => patch(item.key, { name: e.target.value })}
                    className={`${inputClass} mt-1`}
                  />
                </label>

                {!folderLocked && (
                  <label className="block">
                    <span className="text-slate-500 text-xs">Folder</span>
                    <select
                      value={item.meta.folder}
                      onChange={(e) => patch(item.key, { folder: e.target.value as DocumentFolder })}
                      className={`${inputClass} mt-1`}
                    >
                      {(Object.keys(FOLDER_LABELS) as DocumentFolder[]).map((f) => (
                        <option key={f} value={f}>
                          {FOLDER_LABELS[f]}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="block">
                  <span className="text-slate-500 text-xs">Type</span>
                  <select
                    value={item.meta.category}
                    onChange={(e) =>
                      patch(item.key, { category: e.target.value as DocumentCategory })
                    }
                    className={`${inputClass} mt-1`}
                  >
                    {FOLDER_CATEGORIES[item.meta.folder].map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block">
                  <span className="text-slate-500 text-xs">Tax year</span>
                  <select
                    value={item.meta.taxYear ?? ''}
                    onChange={(e) =>
                      patch(item.key, {
                        taxYear: e.target.value ? parseInt(e.target.value, 10) : null,
                      })
                    }
                    className={`${inputClass} mt-1`}
                  >
                    <option value="">— none —</option>
                    {TAX_YEARS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </label>

                {!entityLocked && (
                  <label className="block">
                    <span className="text-slate-500 text-xs">
                      Business{item.meta.folder === 'businesses' ? ' (required)' : ''}
                    </span>
                    <select
                      value={item.meta.entityId ?? ''}
                      onChange={(e) => patch(item.key, { entityId: e.target.value || null })}
                      className={`${inputClass} mt-1 ${
                        item.meta.folder === 'businesses' && !item.meta.entityId
                          ? 'border-amber-500'
                          : ''
                      }`}
                    >
                      <option value="">— none —</option>
                      {entities.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.shortName}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {!accountLocked && (
                  <label className="block">
                    <span className="text-slate-500 text-xs">Account</span>
                    <select
                      value={item.meta.accountId ?? ''}
                      onChange={(e) => patch(item.key, { accountId: e.target.value || null })}
                      className={`${inputClass} mt-1`}
                    >
                      <option value="">— none —</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.institutionName} — {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="block">
                  <span className="text-slate-500 text-xs">Source</span>
                  <input
                    value={item.meta.source}
                    onChange={(e) => patch(item.key, { source: e.target.value })}
                    placeholder="Institution, entity, or sender"
                    className={`${inputClass} mt-1`}
                  />
                </label>

                <label className="block sm:col-span-2 lg:col-span-3">
                  <span className="text-slate-500 text-xs">Notes (optional)</span>
                  <input
                    value={item.meta.notes}
                    onChange={(e) => patch(item.key, { notes: e.target.value })}
                    className={`${inputClass} mt-1`}
                  />
                </label>
              </div>

              {accountLocked && item.meta.accountId && (
                <p className="text-slate-500 text-xs mt-2">
                  Filing under {accountLabel(item.meta.accountId)}
                </p>
              )}

              {item.error && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-2">{item.error}</p>
              )}
            </div>
          ))}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-slate-500 text-xs">
              {needsDestination > 0
                ? `${needsDestination} file${needsDestination === 1 ? '' : 's'} filed under Businesses still need a business selected.`
                : `${pending} file${pending === 1 ? '' : 's'} ready.`}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStaged([])}
                disabled={uploading}
                className={secondaryBtn}
              >
                Clear
              </button>
              <button
                onClick={uploadAll}
                disabled={uploading || pending === 0 || needsDestination > 0}
                className={primaryBtn}
              >
                {uploading ? 'Uploading…' : `Upload ${pending} file${pending === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
